import "./insurance-form.css";

import { Form, FormElement } from "@tql/ui-components-react/kendo/form";
import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { InputSuffix } from "@tql/ui-components-react/kendo/inputs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { MessageBox } from "@tql/ui-components-react/tql/layout/beta";
import { trash_01 } from "@tql/ui-components-react/untitled/icons";

import {
	FieldType,
	ValidationInputField,
} from "~/shared/components/fields/validation-input-field.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { getBaseUrl } from "~/shared/utils/get-base-url.ts";

type RadioGroupItem = {
	label: string;
	value: number;
};

const applyToData: RadioGroupItem[] = [
	{ label: "All Carriers", value: 1 },
	{ label: "As Needed", value: 2 },
];

export const insuranceProductType: RadioGroupItem[] = [
	{ label: "New", value: 1 },
	{ label: "Used", value: 2 },
];

const shipmentValueValidator = (value: number | null) => {
	if (!value || value < 1) {
		return "Shipment Value must be greater than 0";
	}
	return "";
};

export const insuranceRadioGroupValidator = (value: number | null) => {
	if (!value) {
		return "Please select an option";
	}
	return "";
};

export type InsuranceFormData = {
	shipmentValue: number;
	applyTo: number;
	product: number;
};

type InsuranceFormProps = {
	className?: string;
	isSubmitting?: boolean;
	submittingLabel?: string;
	title?: string;
	width?: string;
	hideApplyTo?: boolean;
	initialValues?: {
		shipmentValue?: number | null;
		applyTo?: number;
		product?: number;
	};
	onSubmit: (data: InsuranceFormData) => void;
	onClose: () => void;
	onRemove?: () => void;
	errorMessage?: string;
};

/**
 * Presentational insurance form (shipment value, apply-to, product, T&C link).
 * Owns no data-fetching or quote/shipment state — callers supply `initialValues`
 * and handle submission via `onSubmit`.
 */
export const InsuranceForm = ({
	className,
	isSubmitting = false,
	submittingLabel = "Saving insurance",
	title = "Add Insurance",
	width = "min(400px, 90vw)",
	hideApplyTo = false,
	initialValues,
	onSubmit,
	onClose,
	onRemove,
	errorMessage,
}: InsuranceFormProps) => {
	const baseUrl = getBaseUrl();

	const formInitialValues = {
		shipmentValue: initialValues?.shipmentValue,
		applyTo: hideApplyTo ? 1 : initialValues?.applyTo,
		product: initialValues?.product,
	};

	const handleSubmit = (dataItem: Record<string, unknown>) => {
		if (isSubmitting) return;
		onSubmit({
			applyTo: dataItem["applyTo"] as number,
			product: dataItem["product"] as number,
			shipmentValue: dataItem["shipmentValue"] as number,
		});
	};

	const handleClose = () => {
		if (!isSubmitting) onClose();
	};

	return (
		<TraxModal
			className={className}
			onClose={handleClose}
			title={title}
			type={"primary"}
			width={width}
		>
			<Form
				ignoreModified={true}
				initialValues={formInitialValues}
				onSubmit={handleSubmit}
				render={() => {
					return (
						<FormElement>
							{errorMessage ? (
								<MessageBox
									status={"error"}
									title={"Unable to Update Insurance"}
								>
									{errorMessage}
								</MessageBox>
							) : null}
							<div className={"insurance-modal-wrapper"}>
								<div>
									<div className={"shipment-value-usd-wrapper"}>
										<div>
											<ValidationInputField
												fieldType={FieldType.NUMERIC}
												format={"c2"}
												id={"shipmentValue"}
												label={"Shipment Value"}
												name={"shipmentValue"}
												required={true}
												suffix={() => (
													<InputSuffix>
														<span className={"k-input-suffix-text"}>USD</span>
													</InputSuffix>
												)}
												validator={shipmentValueValidator}
											/>
										</div>
									</div>
								</div>
								<div className={"insurance-radio-buttons-wrapper"}>
									{hideApplyTo ? null : (
										<div>
											<ValidationInputField
												data={applyToData}
												fieldType={FieldType.RADIOGROUP}
												id={"applyTo"}
												label={"Apply To"}
												layout={"horizontal"}
												name={"applyTo"}
												required={true}
												validator={insuranceRadioGroupValidator}
											/>
										</div>
									)}
									<div>
										<ValidationInputField
											data={insuranceProductType}
											fieldType={FieldType.RADIOGROUP}
											id={"product"}
											label={"Product"}
											layout={"horizontal"}
											name={"product"}
											required={true}
											validator={insuranceRadioGroupValidator}
										/>
									</div>
								</div>
								<a
									className={"tql-link"}
									href={`${baseUrl}/documents/Arch_Insurance.pdf`}
									rel={"noopener noreferrer"}
									target={"_blank"}
								>
									Insurance Terms And Conditions
								</a>
							</div>
							<div className={"insurance-modal-actions"}>
								{isSubmitting ? (
									<div
										aria-label={submittingLabel}
										className={"insurance-form-loading"}
										role={"status"}
									>
										<Loader size={"small"} type={"infinite-spinner"} />
									</div>
								) : null}
								{onRemove ? (
									<Button
										disabled={isSubmitting}
										name={"remove"}
										onClick={onRemove}
										svgIcon={trash_01}
										variant={"danger"}
									>
										Remove
									</Button>
								) : null}
								<div className={"insurance-modal-action-group"}>
									<Button
										disabled={isSubmitting}
										name={"download"}
										onClick={handleClose}
									>
										Cancel
									</Button>
								</div>
								<div>
									<Button
										disabled={isSubmitting}
										formAction={"submit"}
										variant={"primary"}
									>
										Save
									</Button>
								</div>
							</div>
						</FormElement>
					);
				}}
			/>
		</TraxModal>
	);
};
